"use client";

import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { SignupFormValues } from "@/shared/lib/validation/user-schemas";
import { emailSendVerification } from "@/server-action/email/email-send-verification";
import { useTransition, useState } from "react";
import { createAppError, AppError, AppErrorType } from "@/shared/lib/errors/error-types";
import { EmailError } from "@/shared/ui/email-error";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

interface EmailStepProps {
  onClickSendEmail: () => void;
}

export function EmailStep({ onClickSendEmail }: EmailStepProps) {
  const router = useRouter();
  const t = useTranslations();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<AppError | null>(null);
  const form = useFormContext<SignupFormValues>();

  const handleClickSendEmail = async () => {
    const isValid = await form.trigger("email");

    if (isValid) {
      startTransition(async () => {
        try {
          const result = await emailSendVerification(form.getValues("email"), "signup");
          if (!result.ok) {
            throw new Error(result.error.message);
          }
          onClickSendEmail();
        } catch (error: unknown) {
          if (error instanceof Error) {
            const appError = createAppError(error.message);
            setError(appError);
          } else {
            setError(createAppError("Unknown error"));
          }
        }
      });
    }
  };

  return (
    <div className="pc:max-w-[420px] w-full">
      {error && error.type === AppErrorType.EMAIL_ALREADY_EXISTS ? (
        <EmailError
          title={t("f_asu_title")}
          description={t("f_asu_cont")}
          button1={{ label: t("f_asu_btn_login"), onClick: () => router.push("/login") }}
          button2={{ label: t("f_asu_btn_pf"), onClick: () => router.push("/find-password") }}
        />
      ) : (
        <>
          <h3 className="heading-3-bold tb:mb-10 mb-8 text-center text-gray-900">{t("f_jm_title")}</h3>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="email" className="body-md-bold text-gray-900">
                  {t("f_jm_lable_id")}
                </FormLabel>
                <FormControl>
                  <Input id="email" type="email" variant="public" placeholder={t("f_jm_placeholder_id")} {...field} />
                </FormControl>
                <FormMessage className="body-sm-regular text-red-500" />
              </FormItem>
            )}
          />

          <Button
            type="button"
            className="mt-10 w-full"
            disabled={isPending}
            variant="primary"
            size="lg"
            onClick={handleClickSendEmail}
          >
            {t("f_jm_btn")}
          </Button>
        </>
      )}
    </div>
  );
}
